import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import { FeatureRequestDetail, STATUS_LABELS } from '../types/featureRequests';
import StatusPill from '../components/featureRequests/StatusPill';
import CategoryChip from '../components/featureRequests/CategoryChip';
import VoteButton from '../components/featureRequests/VoteButton';
import ProgressTrack from '../components/featureRequests/ProgressTrack';
import { colors } from '../theme/theme';

const STATUS_FLOW = ['under_review', 'planned', 'in_progress', 'shipped'] as const;

const FeatureRequestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<FeatureRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.get<FeatureRequestDetail>(API_ENDPOINTS.FEATURE_REQUESTS.DETAIL(id));
      setRequest(data);
    } catch (err: any) {
      setError(err?.message || 'Could not load this request.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleVoteToggle = async () => {
    if (!request || !id) return;
    const nextHasVoted = !request.hasVoted;
    const delta = nextHasVoted ? 1 : -1;
    setRequest({ ...request, hasVoted: nextHasVoted, voteCount: request.voteCount + delta });
    try {
      if (nextHasVoted) {
        // Pinned to the account's own studio under "All locations".
        await apiService.post(API_ENDPOINTS.FEATURE_REQUESTS.VOTE(id), undefined, apiService.atOwnStudio());
      } else {
        await apiService.delete(API_ENDPOINTS.FEATURE_REQUESTS.VOTE(id), apiService.atOwnStudio());
      }
    } catch {
      setRequest((prev) => (prev ? { ...prev, hasVoted: request.hasVoted, voteCount: request.voteCount } : prev));
    }
  };

  const handlePostComment = async () => {
    if (!id || !comment.trim()) return;
    setPosting(true);
    try {
      await apiService.post(
        API_ENDPOINTS.FEATURE_REQUESTS.COMMENTS(id),
        { body: comment.trim() },
        apiService.atOwnStudio(),
      );
      setComment('');
      fetchDetail();
    } catch {
      // The composer keeps the draft so the studio can retry.
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !request) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/feature-requests')} sx={{ mb: 2 }}>
          Back to Feature Requests
        </Button>
        <Alert severity="error">{error || 'Request not found.'}</Alert>
      </Box>
    );
  }

  const activeStep = STATUS_FLOW.indexOf(request.status as (typeof STATUS_FLOW)[number]);
  const isTerminalOther = request.status === 'not_planned' || request.status === 'custom_dev';
  const cardSx = {
    borderRadius: '18px',
    border: `1px solid ${colors.divider}`,
    boxShadow: `0 4px 24px ${colors.primary}14`,
  };

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/feature-requests')} sx={{ mb: 2.5, fontWeight: 600, color: colors.greyText }}>
        Back to Feature Requests
      </Button>

      <Card sx={{ ...cardSx, mb: 2.5 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', gap: 2.25, alignItems: 'flex-start' }}>
            <VoteButton voteCount={request.voteCount} hasVoted={request.hasVoted} onToggle={handleVoteToggle} size={64} />
            <Box sx={{ flex: 1, pt: 0.25 }}>
              <Typography sx={{ fontSize: 23, fontWeight: 800, color: '#1a1a20', lineHeight: 1.3, letterSpacing: '-0.3px' }}>
                {request.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5, alignItems: 'center' }}>
                <StatusPill status={request.status} />
                <CategoryChip category={request.category} />
                {request.alsoRequestedByCount > 0 && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 11px', borderRadius: '20px', backgroundColor: '#fdf1f5' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={colors.secondary} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><circle cx="17" cy="9" r="2.5" /><path d="M15.5 14a5 5 0 0 1 5.5 5" />
                    </svg>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#ad3f68' }}>
                      Also wanted by {request.alsoRequestedByCount} {request.alsoRequestedByCount === 1 ? 'studio' : 'studios'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {!isTerminalOther && (
            <Box sx={{ mt: 4.5 }}>
              <ProgressTrack stages={STATUS_FLOW.map((s) => STATUS_LABELS[s])} activeIndex={activeStep} />
            </Box>
          )}

          <Box sx={{ height: '1px', backgroundColor: colors.divider, my: 3.5 }} />

          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.greyText3, letterSpacing: '0.6px', textTransform: 'uppercase', mb: 1 }}>
            Description
          </Typography>
          <Typography sx={{ fontSize: 14.5, whiteSpace: 'pre-wrap', color: '#3a3a44', lineHeight: 1.65 }}>
            {request.description}
          </Typography>

          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.greyText3, letterSpacing: '0.6px', textTransform: 'uppercase', mt: 2.75, mb: 1 }}>
            Why this is needed
          </Typography>
          <Typography sx={{ fontSize: 14.5, whiteSpace: 'pre-wrap', color: '#3a3a44', lineHeight: 1.65 }}>
            {request.whyNeeded}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 4 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1a1a20', mb: 2.5 }}>
            Comments {request.comments.length > 0 ? `(${request.comments.length})` : ''}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
            {request.comments.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No comments yet. Be the first to add context.
              </Typography>
            )}
            {request.comments.map((c) => (
              <Box key={c._id} sx={{ display: 'flex', gap: 1.5 }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: c.isAdmin ? '#000000' : colors.secondary, fontSize: 13, fontWeight: 700 }}>
                  {c.isAdmin ? 'SS' : (c.authorStudioId?.studioName || '?').charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1a1a20' }}>
                      {c.isAdmin ? 'Sweat Social team' : c.authorStudioId?.studioName || 'A studio'}
                    </Typography>
                    {c.isAdmin && (
                      <Box sx={{ display: 'inline-flex', px: '7px', py: '2px', borderRadius: '6px', backgroundColor: colors.backgroundTertiary }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: colors.greyText, letterSpacing: '0.3px' }}>TEAM</Typography>
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 12, color: colors.greyText3 }}>
                      {format(new Date(c.createdAt), 'MMM d, yyyy h:mm a')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13.5, color: '#3a3a44', whiteSpace: 'pre-wrap', mt: 0.4, lineHeight: 1.55 }}>
                    {c.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder="Add a comment…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px', backgroundColor: colors.backgroundTertiary, fontSize: 13.5 } }}
            />
            <Box
              onClick={!posting && comment.trim() ? handlePostComment : undefined}
              sx={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: colors.primary,
                boxShadow: `0 4px 12px ${colors.primary}66`,
                cursor: posting || !comment.trim() ? 'default' : 'pointer',
                opacity: posting || !comment.trim() ? 0.5 : 1,
                transition: 'box-shadow 150ms ease, transform 150ms ease',
                '&:hover': posting || !comment.trim() ? undefined : { boxShadow: `0 8px 20px ${colors.primary}80`, transform: 'translateY(-1px)' },
              }}
            >
              {posting ? (
                <CircularProgress size={16} sx={{ color: '#ffffff' }} />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeatureRequestDetailPage;
